// Daily Aptitude Drill — Dashboard strip.
// Single-row action strip ("🧠 Today's Aptitude · 2/5    Start ›").
// All three states (fresh / partial / done) share the same one-line shape
// so the Dashboard above-the-fold doesn't inflate vertically.
//
// Polls /student/aptitude-drill/today on mount + every 5 min + on window focus.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { aptitudeDrillAPI, DrillTodayResponse } from '../services/aptitudeDrillAPI';
import { track } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

const POLL_MS = 5 * 60 * 1000;

const AptitudeDrillCard: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const [drill, setDrill] = useState<DrillTodayResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await aptitudeDrillAPI.today();
        if (!cancelled) setDrill(data);
      } catch {
        // Silent — the strip just stays hidden if we can't load it
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const id = window.setInterval(load, POLL_MS);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // Don't render anything until we know what state to show — avoids
  // a jarring flash of "Start" before we know it's already done.
  if (loading || !drill) return null;

  const answered = drill.progress.answered;
  const total = drill.total_questions;
  const completed = drill.status === 'completed' || answered >= total;

  const variant: 'fresh' | 'partial' | 'done' = completed
    ? 'done'
    : answered > 0
      ? 'partial'
      : 'fresh';

  // The whole strip is the CTA — copy is short, state-dependent, single row.
  // Pieces:
  //   [emoji]  Today's Aptitude · N/total          [action label] ›
  const emoji = completed ? '✓' : answered > 0 ? '•' : '🧠';
  const actionLabel = completed ? 'Done' : answered > 0 ? 'Continue' : 'Start';
  const counter = `${answered}/${total}`;

  const palettes = {
    fresh: {
      bg: '#ecfdf5',
      border: '#86efac',
      emoji: '#4a7c10',
      action: '#15803d',
    },
    partial: {
      bg: '#fef9c3',
      border: '#fde047',
      emoji: '#a16207',
      action: '#a16207',
    },
    done: {
      bg: '#f1f5f9',
      border: '#cbd5e1',
      emoji: '#16a34a',
      action: '#16a34a',
    },
  } as const;
  // Dark-mode palettes: tinted fills / brighter fg so the strip reads on a
  // dark page instead of glaring white.
  const darkPalettes = {
    fresh: {
      bg: 'rgba(134, 194, 50, 0.12)',
      border: 'rgba(134, 194, 50, 0.35)',
      emoji: '#9ede4a',
      action: '#7cc95a',
    },
    partial: {
      bg: 'rgba(245, 176, 65, 0.12)',
      border: 'rgba(245, 176, 65, 0.35)',
      emoji: '#f5c563',
      action: '#f5c563',
    },
    done: {
      bg: 'var(--bg-inset)',
      border: 'var(--border-default)',
      emoji: '#6bd08a',
      action: '#6bd08a',
    },
  } as const;
  const p = (dark ? darkPalettes : palettes)[variant];
  const textPrimary = dark ? '#e6e8eb' : '#0f172a';
  const textMuted = dark ? '#aab1ba' : '#64748b';

  return (
    <button
      onClick={() => {
        track('aptitude_drill_card.clicked', {
          variant,
          answered,
          total,
          path_day_index: drill.path?.day_index,
        });
        navigate('/aptitude-drill/today?from=card');
      }}
      type="button"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '10px 14px',
        background: p.bg,
        border: `1.5px solid ${p.border}`,
        borderRadius: 12,
        cursor: completed ? 'default' : 'pointer',
        marginBottom: 10,
        fontFamily: 'inherit',
      }}
      aria-label={`${actionLabel} today's Aptitude drill — ${counter}`}
    >
      <span
        aria-hidden="true"
        style={{
          width: 22,
          textAlign: 'center',
          fontSize: 16,
          color: p.emoji,
          fontWeight: 700,
        }}
      >
        {emoji}
      </span>
      <span
        style={{
          flex: 1,
          textAlign: 'left',
          color: textPrimary,
          fontWeight: 600,
          fontSize: 14,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        Today's Aptitude <span style={{ color: textMuted, fontWeight: 500 }}>· {counter}</span>
      </span>
      <span
        style={{
          color: p.action,
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: 0.1,
        }}
      >
        {actionLabel}
      </span>
      <span style={{ color: textMuted, fontSize: 18, lineHeight: 1, marginLeft: 2 }}>›</span>
    </button>
  );
};

export default AptitudeDrillCard;
